/**
 * TimeZoneSection Component
 * Select preferred time zone with device auto-detect option
 */

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import {
  formatTimezoneLabel,
  getNormalizedTimezone,
  getTimezones,
  normalizeTimezone,
  type TimezoneData,
} from "@/utils/timezone"
import { useUpdateSettingsProfile } from "../hooks/useSettings"

interface TimezoneOption {
  value: string
  label: string
}

export function TimeZoneSection() {
  const { t } = useTranslation("settings")
  const user = useAuthStore(state => state.user)
  const { mutate: updateProfile, isPending } = useUpdateSettingsProfile()

  const [timezone, setTimezone] = useState(
    user?.timezone ? normalizeTimezone(user.timezone) : getNormalizedTimezone(),
  )
  const [useDeviceTimezone, setUseDeviceTimezone] = useState(user?.useDeviceTimezone ?? false)
  const [timezones, setTimezones] = useState<TimezoneOption[]>([])
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true)

  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const data = await getTimezones()
        const options: TimezoneOption[] = data.map((tz: TimezoneData) => ({
          value: tz.tzCode,
          label: formatTimezoneLabel(tz),
        }))
        // Sort by offset (most negative to most positive)
        options.sort((a, b) => {
          const tzA = data.find(t => t.tzCode === a.value)
          const tzB = data.find(t => t.tzCode === b.value)
          return (tzA?.currentOffset ?? 0) - (tzB?.currentOffset ?? 0)
        })
        setTimezones(options)
      } catch (error) {
        console.error("Failed to load timezones:", error)
      } finally {
        setIsLoadingTimezones(false)
      }
    }

    loadTimezones()
  }, [])

  // Sync timezone and useDeviceTimezone from auth store when it changes
  useEffect(() => {
    if (user?.timezone) {
      setTimezone(normalizeTimezone(user.timezone))
    }
    if (user?.useDeviceTimezone !== undefined) {
      setUseDeviceTimezone(user.useDeviceTimezone)
    }
  }, [user])

  const handleUseDeviceToggle = (checked: boolean) => {
    setUseDeviceTimezone(checked)
    if (checked) {
      const deviceTz = getNormalizedTimezone()
      setTimezone(deviceTz)
    }
  }

  const handleSave = () => {
    updateProfile({ timezone, useDeviceTimezone })
  }

  return (
    <div className="flex flex-col gap-7.5">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl font-semibold leading-5 text-primary">
          {t("timezone.title")}
        </h1>
        <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("timezone.description")}</p>
      </div>

      <div className="flex flex-col gap-6.25 md:max-w-md">
        {/* Time Zone Select */}
        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#F2F0EB80]">{t("timezone.title")}</Label>
          <Select
            value={timezone}
            onValueChange={setTimezone}
            disabled={useDeviceTimezone || isLoadingTimezones}
          >
            <SelectTrigger className="w-full border-b rounded-none p-0">
              <SelectValue
                placeholder={
                  isLoadingTimezones ? t("timezone.loading") : t("timezone.selectPlaceholder")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {timezones.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-7.5">
          {/* Use Device Time Zone */}
          <div className="flex items-center gap-2.5">
            <Checkbox
              id="use-device-tz"
              className="w-6 h-6"
              checked={useDeviceTimezone}
              onCheckedChange={checked => handleUseDeviceToggle(checked === true)}
            />
            <Label
              htmlFor="use-device-tz"
              className="text-sm font-semibold leading-6.25 text-primary cursor-pointer"
            >
              {t("timezone.useDeviceTimezone")}
            </Label>
          </div>

          <Button
            variant="primary"
            size="md"
            className="w-full md:w-[220px] h-12.5 uppercase"
            onClick={handleSave}
            isLoading={isPending}
          >
            {t("timezone.save")}
          </Button>
        </div>
      </div>
    </div>
  )
}
